'use client';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import {
  type ComponentPropsWithoutRef,
  cloneElement,
  type ElementRef,
  forwardRef,
  isValidElement,
  type ReactNode,
} from 'react';

import { Spinner } from '@air/primitive-spinner';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const button = tailwindVariants({
  base: 'relative flex shrink-0 items-center justify-center font-semibold transition-colors hover:no-underline disabled:cursor-not-allowed disabled:ads:bg-subtle disabled:bg-gray-3 disabled:ads:text-primary disabled:text-gray-12',
  compoundVariants: [
    {
      appearance: 'filled',
      class:
        'ads:bg-primary bg-blue-9 text-white hover:ads:bg-hover-primary hover:bg-blue-10 hover:text-white active:ads:bg-hover-primary active:bg-blue-10 disabled:ads:bg-subtle disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'blue',
    },
    {
      appearance: 'filled',
      class:
        'ads:bg-ads-violet-9 bg-violet-9 text-white hover:ads:bg-ads-violet-10 hover:bg-violet-10 hover:text-white active:ads:bg-ads-violet-10 active:bg-violet-10',
      color: 'violet',
    },
    {
      appearance: 'filled',
      class:
        'border ads:border-ads-gray-5 border-gray-5 ads:bg-subtle bg-gray-3 ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:ads:bg-subtle disabled:ads:text-primary disabled:hover:ads:bg-subtle',
      color: 'grey',
    },
    {
      appearance: 'filled',
      class:
        'border ads:border-ads-gray-5 border-gray-5 ads:bg-subtle bg-gray-3 ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:ads:bg-subtle disabled:ads:text-primary disabled:hover:ads:bg-subtle',
      color: 'gray',
    },
    {
      appearance: 'filled',
      class:
        'ads:bg-error bg-red-9 text-white hover:ads:bg-ads-flash-10 hover:bg-red-10 hover:text-white active:ads:bg-ads-flash-10 active:bg-red-10 disabled:ads:bg-subtle disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'red',
    },
    {
      appearance: 'ghost',
      class:
        'ads:text-accent text-blue-11 hover:ads:bg-primary-subtle hover:bg-blue-1 active:ads:bg-primary-subtle active:bg-blue-1 disabled:ads:bg-transparent disabled:ads:text-ads-gray-10 disabled:active:ads:bg-transparent disabled:hover:ads:bg-transparent',
      color: 'blue',
    },
    {
      appearance: 'ghost',
      class:
        'ads:text-ads-violet-11 text-violet-11 hover:ads:bg-ads-violet-1 hover:bg-violet-1 active:ads:bg-ads-violet-1 active:bg-violet-1',
      color: 'violet',
    },
    {
      appearance: 'ghost',
      class:
        'ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:active:ads:bg-transparent disabled:hover:ads:bg-transparent',
      color: 'grey',
    },
    {
      appearance: 'ghost',
      class:
        'ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:active:ads:bg-transparent disabled:hover:ads:bg-transparent',
      color: 'gray',
    },
    {
      appearance: 'ghost',
      class:
        'ads:text-error text-red-11 hover:ads:bg-error-subtle hover:bg-red-2 active:ads:bg-error-subtle active:bg-red-2 disabled:ads:bg-transparent disabled:ads:text-ads-gray-10 disabled:active:ads:bg-transparent disabled:hover:ads:bg-transparent',
      color: 'red',
    },
    {
      appearance: 'outline',
      class:
        'ads:border-primary border-blue-11 ads:text-accent text-blue-11 hover:ads:bg-primary-subtle hover:bg-blue-1 active:ads:bg-primary-subtle active:bg-blue-1 disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'blue',
    },
    {
      appearance: 'outline',
      class:
        'ads:border-ads-violet-5 border-violet-5 ads:text-ads-violet-12 text-violet-12 hover:ads:bg-ads-violet-1 hover:bg-violet-1 active:ads:bg-ads-violet-1 active:bg-violet-1',
      color: 'violet',
    },
    {
      appearance: 'outline',
      class:
        'ads:border-ads-gray-5 border-gray-5 ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'grey',
    },
    {
      appearance: 'outline',
      class:
        'ads:border-ads-gray-5 border-gray-5 ads:text-secondary text-gray-11 hover:ads:bg-hover-subtle hover:bg-gray-5 active:ads:bg-hover-subtle active:bg-gray-4 disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'gray',
    },
    {
      appearance: 'outline',
      class:
        'ads:border-error border-red-11 ads:text-error text-red-11 hover:ads:bg-error-subtle hover:bg-red-2 active:ads:bg-error-subtle active:bg-red-2 disabled:ads:text-primary disabled:active:ads:bg-subtle disabled:hover:ads:bg-subtle',
      color: 'red',
    },
  ],
  defaultVariants: {
    appearance: 'filled',
    color: 'blue',
    size: 'medium',
  },
  variants: {
    appearance: {
      filled: 'border-0',
      ghost: 'border-0 bg-transparent disabled:bg-transparent disabled:ads:text-ads-gray-10 disabled:text-gray-10',
      outline: 'border bg-transparent disabled:border-0',
    },
    color: {
      blue: '',
      gray: '',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: '',
      red: '',
      violet: '',
    },
    size: {
      'extra-large': 'h-12 rounded-8 px-4 text-16',
      'extra-small': 'h-4 rounded-4 px-2 text-10',
      large: 'h-10 rounded-8 px-3 text-14',
      medium: 'h-8 rounded-8 px-2.5 text-14',
      small: 'h-6 rounded-4 px-2 text-12',
    },
  },
});

export const buttonSpinner = tailwindVariants({
  defaultVariants: {
    size: 'medium',
  },
  variants: {
    size: {
      'extra-large': 'h-5 w-5',
      'extra-small': 'h-2 w-2',
      large: 'h-5 w-5',
      medium: 'h-4 w-4',
      small: 'h-3 w-3',
    },
  },
});

export type ButtonVariants = VariantProps<typeof button>;

export type ButtonSpinnerVariants = VariantProps<typeof buttonSpinner>;

export const BUTTON_ELEMENT_TAG = 'button';

export type ButtonProps = Omit<ComponentPropsWithoutRef<typeof BUTTON_ELEMENT_TAG>, 'prefix' | 'suffix'> &
  ButtonVariants & {
    asChild?: boolean;
    isLoading?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
  };

type ButtonLeadingContentParams = {
  hideSpinnerFromA11y?: boolean;
  isLoading?: boolean;
  isSmall: boolean;
  prefix?: ReactNode;
  size: ButtonVariants['size'];
};

const getButtonLeadingContent = ({
  hideSpinnerFromA11y,
  isLoading,
  isSmall,
  prefix,
  size,
}: ButtonLeadingContentParams) => {
  if (isLoading) {
    return (
      <div aria-live="polite" className={classNames(isSmall ? 'mr-1' : 'mr-2')} role="status">
        <VisuallyHidden>Loading...</VisuallyHidden>
        <Spinner aria-hidden={hideSpinnerFromA11y} className={buttonSpinner({ size })} />
      </div>
    );
  }

  if (prefix) {
    return <div className={classNames(isSmall ? 'mr-1 shrink-0' : 'mr-2 shrink-0')}>{prefix}</div>;
  }

  return null;
};

export const Button = forwardRef<ElementRef<typeof BUTTON_ELEMENT_TAG>, ButtonProps>(
  (
    {
      appearance,
      asChild,
      children,
      className,
      color,
      disabled,
      isLoading,
      prefix,
      size,
      suffix,
      type = 'button',
      ...restOfProps
    },
    forwardedRef,
  ) => {
    // biome-ignore lint/suspicious/noExplicitAny: <https://linear.app/air/issue/ENG-132/update-button-to-use-primitive-and-remove-prefix-and-suffix-so-we-dont>
    const Component: any = asChild ? Slot : BUTTON_ELEMENT_TAG;
    const isSmall = !!size && ['extra-small', 'small'].includes(size);
    const asChildLeading = getButtonLeadingContent({ isLoading, isSmall, prefix, size });
    const defaultLeading = getButtonLeadingContent({
      hideSpinnerFromA11y: true,
      isLoading,
      isSmall,
      prefix,
      size,
    });

    const childrenNode =
      // biome-ignore lint/suspicious/noExplicitAny: <https://linear.app/air/issue/ENG-132/update-button-to-use-primitive-and-remove-prefix-and-suffix-so-we-dont>
      asChild && isValidElement<any>(children) ? (
        cloneElement(children, {
          children: (
            <>
              {asChildLeading}
              {children.props.children}
              {suffix ? <div className={classNames(isSmall ? 'ml-1 shrink-0' : 'ml-2 shrink-0')}>{suffix}</div> : null}
            </>
          ),
        })
      ) : (
        <>
          {defaultLeading}
          {children}
          {suffix ? <div className={classNames(isSmall ? 'ml-1 shrink-0' : 'ml-2 shrink-0')}>{suffix}</div> : null}
        </>
      );

    return (
      <Component
        className={classNames(
          button({ appearance, class: className, color, size }),
          isLoading || disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        disabled={disabled}
        ref={forwardedRef}
        type={type}
        {...restOfProps}
      >
        {childrenNode}
      </Component>
    );
  },
);

Button.displayName = 'Button';
